import { Button, DataButton } from "@sellar-io/components";
import {
  BuyerRelationshipListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../../../../../components";
import { ActionPanel } from "../../../../../components/actionPanel/ActionPanel";
import { AddToFavouritesGroup } from "../../../../../components/addToFavouritesButtons/AddToFavouritesGroup";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../../components/skeleton/Skeleton";
import * as ROUTES from "../../../../../constants/routes";
import {
  SellarContextActionEnum,
  useSellarContext,
  useSellarUser,
} from "../../../../../hooks";
import { useBuyerRelationshipCreate } from "../../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipCreate";
import { useBuyerRelationshipList } from "../../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { RemoveFromListDialog } from "../../../../buyer/connectedSuppliers/Dialogs/RemoveFromListDialog";
import { StorefrontMessageDialog } from "../StorefrontDialogs/StorefrontMessagesDialog/StorefrontMessagesDialog";

export const StorefrontSupplierBannerActionPanel = ({
  supplier,
}: {
  supplier: SupplierGetResponseType | undefined;
}): ReactElement | null => {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const [_, dispatch] = useSellarContext();
  const navigate = useNavigate();

  const buyerRelationshipListQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplier?.id ?? 0,
        },
      ],
      limit: 1,
    },

    {
      enabled: !!business?.Retailer && !!supplier?.id && !!user,
    },
  );

  const buyerRelationship = buyerRelationshipListQuery.data?.pages[0]?.[0];

  if (
    (!user && !supplier) ||
    (user &&
      (!supplier ||
        (buyerRelationshipListQuery.isLoading &&
          buyerRelationshipListQuery.fetchStatus !== "idle")))
  )
    return (
      <SkeletonContainer className={"mt-4"}>
        <SkeletonText className={"h-[50px] w-[50%]"} />
      </SkeletonContainer>
    );

  return (
    <>
      <div className="flex flex-wrap-reverse items-center gap-2">
        {business?.Retailer && (
          <>
            {supplier ? (
              <AddToListButtons
                supplier={supplier}
                buyerRelationship={buyerRelationship}
              />
            ) : null}
          </>
        )}
        {business?.Supplier && business.Supplier.id !== supplier?.id && (
          <ActionPanel
            className={"w-full"}
            title={`Looking to order from ${supplier?.Business.name}?`}
            message={
              "Switch to your buyer account. If you don't have one, you can set one up in seconds and continue using your current login details"
            }
          >
            <div className="mt-2 flex w-full flex-wrap gap-2">
              <Button
                className={"w-full lg:w-auto"}
                onClick={() =>
                  dispatch({
                    type: SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE,
                    businessSwitcherActive: true,
                  })
                }
              >
                Switch & Place Order
              </Button>
              <Button
                className={"w-full lg:w-auto"}
                onClick={() => {
                  navigate(ROUTES.NEW_VENUE);
                }}
                color={"secondary"}
              >
                Create buyer account
              </Button>
            </div>
          </ActionPanel>
        )}
      </div>
    </>
  );
};

const AddToListButtons = ({
  buyerRelationship,
  supplier,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number] | undefined;
  supplier: SupplierGetResponseType;
}) => {
  const [messageDialogActive, setMessageDialogActive] = useState(false);
  const [removeFromListDialogActive, setRemoveFromListDialogActive] =
    useState(false);
  const [addedToList, setAddedToList] = useState(!!buyerRelationship?.id);
  const addToListMutation = useBuyerRelationshipCreate();
  const addToList = () => {
    return addToListMutation.mutate(
      {
        SupplierId: supplier.id,
      },
      {
        onSuccess: () => setAddedToList(true),
      },
    );
  };

  const isPending = addToListMutation.isPending;
  return (
    <>
      <div className={"flex shrink-0 flex-col gap-2 md:flex-row"}>
        <div className="flex flex-row gap-2">
          <DataButton
            active={!!buyerRelationship?.id}
            className={"w-auto"}
            onClick={() =>
              buyerRelationship?.id
                ? setRemoveFromListDialogActive(true)
                : addToList()
            }
            border={true}
            loading={isPending}
          >
            <Icon icon={addedToList ? "CHECK" : "PLUS"} />
            {addedToList ? "Added to list" : "Add to list"}
          </DataButton>
          <AddToFavouritesGroup buyerRelationship={buyerRelationship} />
        </div>
        <div className="">
          {buyerRelationship?.id && (
            <DataButton onClick={() => setMessageDialogActive(true)}>
              <Icon icon={"MESSAGE_OUTLINE"} />
              Contact Supplier
            </DataButton>
          )}
        </div>
      </div>
      {buyerRelationship && (
        <RemoveFromListDialog
          active={removeFromListDialogActive}
          onDismiss={() => setRemoveFromListDialogActive(false)}
          onSubmit={() => setAddedToList(false)}
          buyerRelationshipId={buyerRelationship.id}
        />
      )}
      {!!buyerRelationship && (
        <StorefrontMessageDialog
          relationshipId={buyerRelationship.RelationshipId}
          active={messageDialogActive}
          onDismiss={() => setMessageDialogActive(false)}
          supplierName={supplier.Business.name}
        />
      )}
    </>
  );
};
